/**
 * TS-side mirror of the WGSL `FrameUniforms` struct in shaders/common.wgsl.
 *
 * Layout must stay in lockstep with the shader. The struct is 64 bytes,
 * encoded as 16 little-endian f32s (with frameIndex aliased over a u32 slot
 * via Uint32Array view onto the same buffer).
 */

export const FRAME_UNIFORMS_BYTES = 64;
export const FRAME_UNIFORMS_F32_COUNT = 16;

// Field offsets (in f32 units, i.e. byte_offset / 4).
const O_RES = 0; // 2 floats
const O_INV_RES = 2; // 2 floats
const O_ASPECT = 4;
const O_TIME = 5;
const O_DT = 6;
const O_FRAME_INDEX = 7; // u32
const O_RMS = 8;
const O_BASS = 9;
const O_MID = 10;
const O_TREBLE = 11;
const O_BEAT_TICK = 12;
const O_BEAT_CONFIDENCE = 13;
const O_BASS1S = 14;
const O_RMS1S = 15;

export type FrameUniformValues = {
  resolution: [number, number];
  aspect: number;
  time: number;
  dt: number;
  frameIndex: number;
  rms: number;
  bass: number;
  mid: number;
  treble: number;
  beatTick: number;
  beatConfidence: number;
  bass1s: number;
  rms1s: number;
};

export class FrameUniforms {
  readonly buffer: GPUBuffer;
  private readonly cpu: ArrayBuffer;
  private readonly f32: Float32Array;
  private readonly u32: Uint32Array;

  constructor(device: GPUDevice, label = 'frame-uniforms') {
    this.buffer = device.createBuffer({
      label,
      size: FRAME_UNIFORMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.cpu = new ArrayBuffer(FRAME_UNIFORMS_BYTES);
    this.f32 = new Float32Array(this.cpu);
    this.u32 = new Uint32Array(this.cpu);
  }

  write(device: GPUDevice, v: FrameUniformValues): void {
    const f = this.f32;
    const u = this.u32;
    f[O_RES] = v.resolution[0];
    f[O_RES + 1] = v.resolution[1];
    f[O_INV_RES] = v.resolution[0] > 0 ? 1 / v.resolution[0] : 0;
    f[O_INV_RES + 1] = v.resolution[1] > 0 ? 1 / v.resolution[1] : 0;
    f[O_ASPECT] = v.aspect;
    f[O_TIME] = v.time;
    f[O_DT] = v.dt;
    u[O_FRAME_INDEX] = v.frameIndex >>> 0;
    f[O_RMS] = v.rms;
    f[O_BASS] = v.bass;
    f[O_MID] = v.mid;
    f[O_TREBLE] = v.treble;
    f[O_BEAT_TICK] = v.beatTick;
    f[O_BEAT_CONFIDENCE] = v.beatConfidence;
    f[O_BASS1S] = v.bass1s;
    f[O_RMS1S] = v.rms1s;
    device.queue.writeBuffer(this.buffer, 0, this.cpu);
  }
}
